import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";
import { format, isValid, parse } from "date-fns";
import { getPartnerProgram } from "../../helpers/partnerCentral_helper";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  auditHubPageSx,
  outlinedButtonSx,
} from "../Compliance/AuditHub/theme";
import { formFromProgramRecord, toUiProgramType } from "./programPayload";
import { programTypeByCode } from "./programTypes";

const STATUS_TONE = {
  ACTIVE: { bg: "#ecfdf5", fg: "#047857" },
  DRAFT: { bg: "#fffbeb", fg: "#b45309" },
  RETIRED: { bg: "#eff6ff", fg: "#2563eb" },
  ARCHIVED: { bg: "#f1f5f9", fg: "#475569" },
};

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = parse(String(value).slice(0, 10), "yyyy-MM-dd", new Date());
  return isValid(parsed) ? format(parsed, "MM/dd/yyyy") : String(value);
};

const display = (value) => {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const StatusChip = ({ status }) => {
  const tone = STATUS_TONE[status] || STATUS_TONE.ARCHIVED;
  return (
    <Chip
      size="small"
      label={status || "—"}
      sx={{
        height: 22,
        fontSize: "0.75rem",
        fontWeight: 600,
        borderRadius: "6px",
        backgroundColor: tone.bg,
        color: tone.fg,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

/** Same visual language as Selected Accounts: payer chips labeled + accent-highlighted. */
const AccountChips = ({ items }) => {
  if (!items?.length) {
    return (
      <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: AH_TEXT }}>—</Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {items.map((item) => {
        const number = item.accountNumber;
        const isPayer = Boolean(item.isPayer);
        return (
          <Chip
            key={`${isPayer ? "payer" : "payee"}-${number}`}
            size="small"
            label={isPayer ? `Payer: ${number}` : number}
            title={isPayer ? `Payer: ${number}` : number}
            sx={{
              height: 24,
              fontSize: 12,
              fontFamily: "monospace",
              fontWeight: 500,
              borderRadius: "6px",
              backgroundColor: isPayer ? "#fff4eb" : AH_TRACK,
              color: AH_TEXT,
              border: `1px solid ${isPayer ? `${AH_ACCENT}55` : AH_BORDER}`,
              maxWidth: "100%",
              "& .MuiChip-label": {
                px: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        );
      })}
    </Box>
  );
};

/** Selected Accounts order: explicit payer chips first, then payees. */
const buildProgramLinkedAccountItems = (form) => {
  const map =
    form?.payerPayeeAccountsMap && typeof form.payerPayeeAccountsMap === "object"
      ? form.payerPayeeAccountsMap
      : {};
  const payerSet = new Set(
    [
      ...(Array.isArray(form?.payeeUiPayerNumbers) ? form.payeeUiPayerNumbers : []),
      ...(Array.isArray(form?.payerAccountNumbers) ? form.payerAccountNumbers : []),
    ]
      .map((id) => String(id || "").trim())
      .filter((id) => /^\d{12}$/.test(id))
  );

  const seen = new Set();
  const ordered = [];
  const pushId = (raw) => {
    const id = String(raw || "").trim();
    if (!/^\d{12}$/.test(id) || seen.has(id)) return;
    seen.add(id);
    ordered.push(id);
  };

  // Prefer wire map values (Selected Accounts chips), including self-payer ids.
  Object.entries(map).forEach(([payer, values]) => {
    const payerId = String(payer || "").trim();
    if (/^\d{12}$/.test(payerId)) payerSet.add(payerId);
    (Array.isArray(values) ? values : []).forEach(pushId);
  });
  (form?.payerAccountNumbers || []).forEach(pushId);
  (form?.payeeAccountNumbers || []).forEach(pushId);

  const payers = ordered.filter((id) => payerSet.has(id));
  const payees = ordered.filter((id) => !payerSet.has(id));
  return [
    ...payers.map((accountNumber) => ({ accountNumber, isPayer: true })),
    ...payees.map((accountNumber) => ({ accountNumber, isPayer: false })),
  ];
};

const DetailRow = ({ label, value, last }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "minmax(150px, 38%) 1fr" },
      gap: { xs: 0.35, sm: 2 },
      alignItems: "start",
      py: 1.25,
      borderBottom: last ? "none" : `1px solid ${AH_BORDER}`,
    }}
  >
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 600,
        color: AH_MUTED,
        lineHeight: 1.45,
        pt: { sm: 0.1 },
      }}
    >
      {label}
    </Typography>
    <Box sx={{ minWidth: 0 }}>
      {typeof value === "string" || typeof value === "number" ? (
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 500,
            color: AH_TEXT,
            lineHeight: 1.45,
            overflowWrap: "anywhere",
          }}
        >
          {value}
        </Typography>
      ) : (
        value
      )}
    </Box>
  </Box>
);

const DetailSection = ({ title, rows }) => {
  if (!rows.length) return null;
  return (
    <Box
      sx={{
        border: `1px solid ${AH_BORDER}`,
        borderRadius: "10px",
        backgroundColor: AH_WHITE,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1,
          backgroundColor: AH_TRACK,
          borderBottom: `1px solid ${AH_BORDER}`,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.02em",
            color: AH_TEXT,
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ px: 1.75 }}>
        {rows.map((row, index) => (
          <DetailRow
            key={row.label}
            label={row.label}
            value={row.value}
            last={index === rows.length - 1}
          />
        ))}
      </Box>
    </Box>
  );
};

const pushIf = (rows, condition, item) => {
  if (condition) rows.push(item);
};

const buildSections = (record, form) => {
  const typeLabel = programTypeByCode(form.programType)?.label || form.programType;
  const status = record?.status || "—";

  const overview = [
    { label: "Program name", value: display(form.displayName) },
    { label: "Program type", value: display(typeLabel) },
    { label: "Agreement ID", value: display(form.agreementId) },
    { label: "Reference Number", value: display(form.referenceNumber) },
    { label: "Status", value: <StatusChip status={status} /> },
  ];

  const termScope = [
    { label: "Start date", value: formatDate(form.startDate) },
    { label: "End date", value: formatDate(form.endDate) },
  ];
  pushIf(termScope, Boolean(form.renewalNotification), {
    label: "Renewal notification",
    value: `${form.renewalNotification} days`,
  });
  pushIf(termScope, Boolean(form.trueUpWindow), {
    label: "True-up window",
    value: display(form.trueUpWindow),
  });
  pushIf(termScope, Boolean(form.expirationAlertWindow), {
    label: "Expiration alert",
    value: `${form.expirationAlertWindow} days`,
  });

  const linkedItems = buildProgramLinkedAccountItems(form);
  const accounts = [
    {
      label: linkedItems.length
        ? `Linked accounts (${linkedItems.length})`
        : "Linked accounts",
      value: <AccountChips items={linkedItems} />,
    },
    {
      label: "Regions",
      value: form.regions?.length ? form.regions.join(", ") : "All regions",
    },
  ];

  const commercial = [];
  pushIf(commercial, Boolean(form.partnerTierLevel), {
    label: "Partner tier level",
    value: display(form.partnerTierLevel),
  });
  pushIf(commercial, Boolean(form.baselineDiscount), {
    label: "Baseline discount",
    value: `${form.baselineDiscount}%`,
  });
  pushIf(commercial, Boolean(form.annualCommitment), {
    label: "Annual commitment",
    value: `$${form.annualCommitment}`,
  });
  pushIf(commercial, Boolean(form.paymentTerms), {
    label: "Payment terms",
    value: display(form.paymentTerms),
  });
  pushIf(commercial, Boolean(form.multiYearCommitment), {
    label: "Multi-year commitment",
    value: `$${form.multiYearCommitment}`,
  });
  pushIf(commercial, Boolean(form.annualConsumptionTarget), {
    label: "Annual consumption target",
    value: `$${form.annualConsumptionTarget}`,
  });
  pushIf(commercial, Boolean(form.edpDiscount), {
    label: "EDP discount",
    value: `${form.edpDiscount}%`,
  });
  pushIf(commercial, Boolean(form.trueUpModel), {
    label: "True-up model",
    value: display(form.trueUpModel),
  });
  pushIf(commercial, Boolean(form.shortfallRule), {
    label: "Shortfall rule",
    value: display(form.shortfallRule),
  });
  pushIf(commercial, form.programType === "EDP", {
    label: "Rollover unused commitment",
    value: display(form.rolloverUnused),
  });
  pushIf(commercial, Boolean(form.riCommitmentType), {
    label: "RI commitment type",
    value: display(form.riCommitmentType),
  });
  pushIf(commercial, Boolean(form.paymentOption), {
    label: "Payment option",
    value: display(form.paymentOption),
  });
  pushIf(commercial, Boolean(form.termLength), {
    label: "Term length",
    value: display(form.termLength),
  });
  pushIf(commercial, Boolean(form.targetCoverage), {
    label: "Target coverage",
    value: `${form.targetCoverage}%`,
  });
  pushIf(commercial, Boolean(form.wasteThreshold), {
    label: "Waste threshold",
    value: `${form.wasteThreshold}%`,
  });
  pushIf(commercial, form.programType === "RI", {
    label: "Cross-account sharing",
    value: display(form.crossAccountSharing),
  });
  pushIf(commercial, form.programType === "SPP", {
    label: "Expiration override",
    value: display(form.expirationOverride),
  });

  const incentives = [];
  pushIf(incentives, Boolean(form.growthTriggerMetric), {
    label: "Growth trigger metric",
    value: display(form.growthTriggerMetric),
  });
  pushIf(incentives, Boolean(form.targetMilestoneValue), {
    label: "Target milestone value",
    value: display(form.targetMilestoneValue),
  });
  pushIf(incentives, Boolean(form.growthBonusRate), {
    label: "Growth bonus rate",
    value: `${form.growthBonusRate}%`,
  });
  pushIf(incentives, Boolean(form.payoutStructure), {
    label: "Payout structure",
    value: display(form.payoutStructure),
  });

  const coverage = [];
  pushIf(coverage, Boolean(form.eligibleServices?.length), {
    label: "Eligible services",
    value: `${form.eligibleServices.length} selected`,
  });
  pushIf(coverage, Boolean(form.qualifyingServices?.length), {
    label: "Qualifying services",
    value: `${form.qualifyingServices.length} selected`,
  });
  pushIf(coverage, Boolean(form.riEligibleServices?.length), {
    label: "RI eligible services",
    value: `${form.riEligibleServices.length} selected`,
  });
  pushIf(coverage, Boolean(form.tiers?.length), {
    label: "Volume tiers",
    value: `${form.tiers.length} tier${form.tiers.length === 1 ? "" : "s"}`,
  });

  return [
    { title: "Overview", rows: overview },
    { title: "Term & scope", rows: termScope },
    { title: "Accounts", rows: accounts },
    { title: "Commercial terms", rows: commercial },
    { title: "Growth incentives", rows: incentives },
    { title: "Coverage", rows: coverage },
  ].filter((section) => section.rows.length > 0);
};

const ProgramDetailsDialog = ({ open, program, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [record, setRecord] = useState(null);

  useEffect(() => {
    if (!open || !program?.id || !program?.program_type) {
      setRecord(null);
      setError("");
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    setRecord(null);
    getPartnerProgram(program.program_type, program.id)
      .then((data) => {
        if (!cancelled) setRecord(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Unable to load program details.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, program?.id, program?.program_type]);

  const form = useMemo(() => {
    if (record) return formFromProgramRecord(record);
    if (!program) return null;
    return formFromProgramRecord({
      ...program,
      program_type: program.program_type,
      program_display_name: program.program_display_name,
      agreement_id: program.agreement_id,
      status: program.status,
      start_date: program.start_date,
      end_date: program.end_date,
    });
  }, [record, program]);

  const sections = useMemo(() => {
    if (!form) return [];
    return buildSections(record || program, form);
  }, [form, record, program]);

  const title =
    form?.displayName ||
    program?.program_display_name ||
    program?.agreement_id ||
    "Program details";
  const typeCode = form?.programType || toUiProgramType(program?.program_type);

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{
        sx: {
          ...auditHubPageSx,
          borderRadius: "12px",
          overflow: "hidden",
          maxWidth: 680,
          maxHeight: { xs: "92vh", sm: "88vh" },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          flexShrink: 0,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
            Program Details
          </Typography>
          <Typography
            sx={{
              mt: 0.25,
              fontSize: 12,
              color: AH_WHITE,
              opacity: 0.92,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
            {typeCode ? ` · ${typeCode === "RI" ? "RI" : typeCode}` : ""}
          </Typography>
        </Box>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          disabled={loading}
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: loading ? "default" : "pointer",
            flexShrink: 0,
          }}
        >
          ×
        </Box>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2.5,
          backgroundColor: AH_WHITE,
          flex: "1 1 auto",
          overflowY: "auto",
        }}
      >
        {loading && !form ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} sx={{ color: AH_ACCENT }} />
          </Box>
        ) : error && !form ? (
          <Typography sx={{ fontSize: 13, color: "#d32f2f" }} role="alert">
            {error}
          </Typography>
        ) : (
          <Box sx={{ display: "grid", gap: 1.75 }}>
            {error ? (
              <Typography sx={{ fontSize: 12, color: "#d32f2f" }} role="alert">
                {error} Showing available summary details.
              </Typography>
            ) : null}
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={14} sx={{ color: AH_MUTED }} />
                <Typography sx={{ fontSize: 12, color: AH_MUTED }}>
                  Loading full program details…
                </Typography>
              </Box>
            ) : null}
            {sections.map((section) => (
              <DetailSection key={section.title} title={section.title} rows={section.rows} />
            ))}
          </Box>
        )}
      </DialogContent>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          px: 3,
          py: 2,
          borderTop: `1px solid ${AH_BORDER}`,
          backgroundColor: "#f8fafc",
          flexShrink: 0,
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          disabled={loading && !form}
          sx={{
            ...outlinedButtonSx,
            backgroundColor: "#74788d",
            color: AH_WHITE,
            borderColor: "#74788d",
            "&:hover": { backgroundColor: "#686c7f", borderColor: "#686c7f", boxShadow: "none" },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

export default ProgramDetailsDialog;
